import { get } from 'lodash';
import { makeRequest } from '@/shared/services/http';
import { ICustomersGetResponse } from '@/shared/endpoints/customers';
import { ITransferPrescriptions } from '@/shared/types/interfaces/transfer-prescriptions';
import { hueOptionsList } from '@/shared/constants/hue-options';
import { checkEligibilityAndSave } from '@/shared/helpers/eligibility';
import { getPreviousTestClaim } from '@/shared/helpers/test-claim';
import dayJS from '@/shared/helpers/dayjs';

const generateNoteRequest = (content: string) => {
    return makeRequest<any>('post', 'customers', 'note', {
        content
    });
};

const buildMedicationDetails = (prescriptions: ITransferPrescriptions[]) => {
    const relevantItemsForOtcNote: { [key: string]: { destructure: string; prefix?: string } } = {
        Medication: { destructure: 'prescribable.name' },
        Quantity: { destructure: 'quantity', prefix: '#' },
        Directions: { destructure: 'direction' },
        Packaging: { destructure: 'packaging' }
    };
    return prescriptions.reduce((acc: string, prescription: ITransferPrescriptions) => {
        return (acc += `• ${Object.keys(relevantItemsForOtcNote)
            .map(item => `${relevantItemsForOtcNote[item].prefix || ''}${get(prescription, relevantItemsForOtcNote[item].destructure, '')}`)
            .join(', ')}\n`);
    }, '');
};

const getEligibilityNotes = async (currentUser?: ICustomersGetResponse) => {
    const eligibilityPassed = await checkEligibilityAndSave();

    if (eligibilityPassed === null || eligibilityPassed === true) return null;

    const insurances = currentUser?.insurances;

    if (!insurances?.length) return null;

    let notes = [...insurances]
        .map(insurance => {
            const claim = getPreviousTestClaim(insurance, currentUser);

            if (!claim) return null as never;

            return `
                Insurance:
                bin: ${insurance.provider.bin}
                group: ${insurance.groupID}
                id: ${insurance.id}
                status: ${claim.prescriptions[0].responseStatus}
                reason: ${claim.prescriptions[0].rejectCodes?.join(', ') || ''}
                message: ${claim.prescriptions[0].additionalMessage}
            `;
        })
        .filter(Boolean)
        .join('\n');

    if (notes) {
        notes = 'Onboarded customer does not have eligible insurance\n\n' + notes;
    }
    return notes;
};

export const completeOnboardingNotesRequest = async (currentUser?: ICustomersGetResponse) => {
    try {
        const selectedHueType = currentUser?.userOnboarding?.details?.medboxHueType;
        const cycleDuration = currentUser?.userOnboarding?.details?.cycleDuration;
        const hueName = hueOptionsList.find(hue => hue.id === selectedHueType)?.name;
        const notePromises = [];
        if (hueName) {
            notePromises.push(generateNoteRequest(`Medbox Hue Color: ${hueName}`));
        }
        if (cycleDuration) {
            notePromises.push(generateNoteRequest(`Cycle Duration: ${cycleDuration}`));
        }

        const eligibilityNotes = await getEligibilityNotes(currentUser);

        eligibilityNotes && notePromises.push(generateNoteRequest(eligibilityNotes));

        const otcTransferPrescriptions = (currentUser?.transferPrescriptions || []).filter(e => e.otc);
        if (otcTransferPrescriptions.length > 0) {
            let noteText = 'OTC Medications: \n';
            noteText += buildMedicationDetails(otcTransferPrescriptions);
            notePromises.push(generateNoteRequest(noteText));
        }
        Promise.all(notePromises);
    } catch (err) {
        console.log('Error in sending notes', err);
    }
};

export const cropBase64ImageWithLine = async (base64: string): Promise<File> => {
    return new Promise(((resolve, reject) => {
        const image = new Image();
        image.src = base64;
        image.onerror = () => {
            reject(new Error('Failed to load image'));
        }
        image.onload = () => {
            // Create a canvas to draw the image on
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (ctx) {

                canvas.width = image.width;
                canvas.height = image.height;


                ctx.drawImage(image, 0, 0);


                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const data = imageData.data;


                let left = canvas.width, top = canvas.height, right = 0, bottom = 0;


                for (let y = 0; y < canvas.height; y++) {
                    for (let x = 0; x < canvas.width; x++) {
                        const index = (y * canvas.width + x) * 4;
                        const alpha = data[index + 3];


                        if (alpha > 0) {
                            if (x < left) left = x;
                            if (x > right) right = x;
                            if (y < top) top = y;
                            if (y > bottom) bottom = y;
                        }
                    }
                }


                const cropWidth = right - left;
                const cropHeight = bottom - top;


                const croppedCanvas = document.createElement('canvas');
                const croppedCtx = croppedCanvas.getContext('2d');
                croppedCanvas.width = cropWidth + 100;
                croppedCanvas.height = cropHeight;


                croppedCtx?.drawImage(
                    canvas,
                    left,
                    top,
                    cropWidth,
                    cropHeight,
                    0,
                    0,
                    cropWidth,
                    cropHeight
                );


                croppedCtx.strokeStyle = 'black';
                croppedCtx.lineWidth = 1;
                croppedCtx.beginPath();
                const lineY = Math.floor(cropHeight * 0.85);
                croppedCtx.moveTo(0, lineY);
                croppedCtx.lineTo(cropWidth, lineY);
                croppedCtx.stroke();

                const today = dayJS().format('MM/DD/YYYY');
                croppedCtx.font = '16px sans-serif';
                croppedCtx.fillStyle = 'black';
                croppedCtx.textBaseline = 'middle';

                croppedCtx.fillText(today, cropWidth + 10, lineY); // 10px to the right of line
                const base64String = croppedCanvas.toDataURL('image/png');
                const arr = base64String.split(",");
                const mime = arr[0].match(/:(.*?);/)[1];
                const bstr = atob(arr[1]);
                let n = bstr.length;
                const u8arr = new Uint8Array(n);

                while (n--) {
                    u8arr[n] = bstr.charCodeAt(n);
                }

                const file = new File([u8arr], 'signature.png', { type: mime });
                resolve(file);
            }
        };
    }));
}